import { lazy } from "react";
import type { RouteObject } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { canAccessAdminTickets, canAccessResidentTickets } from "@/components/ProjectSidebar/permissions";
import { redirectFromLoader } from "@/components/Routing/redirectFromLoader";
import { RequireAuth } from "@/components/Routing/RequireAuth";
import { RolePage } from "@/components/Routing/RolePage";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { getLocalStorageValue } from "@/hooks/useLocalStorage";
import {
  dashboardStartupContext,
  shouldRevalidateOnPathChange,
  whenDashboardProjectReady,
} from "@/providers/dashboardStartup";
import type { ProjectId } from "@/providers/ProjectContext";
import { ticketQueries } from "@/queries/tickets/queryOptions";

import { getStoredTicketFiltersUrl } from "./filters";
import { ticketsRoutes } from "./routes";

// The role is known only once the session user has loaded, after route matching. Load each role's
// page when it renders, so residents do not download the admin ticket pages and vice versa.
const TicketListPage = lazy(() => import("./pages/list/TicketListPage").then((m) => ({ default: m.TicketListPage })));
const ResidentTicketListPage = lazy(() =>
  import("./pages/resident-list/ResidentTicketListPage").then((m) => ({ default: m.ResidentTicketListPage })),
);
const loadTicketDetailsPage = () => import("./pages/details/TicketDetailsPage");
const loadResidentTicketDetailsPage = () => import("./pages/resident-details/ResidentTicketDetailsPage");
const TicketDetailsPage = lazy(() => loadTicketDetailsPage().then((m) => ({ default: m.TicketDetailsPage })));
const ResidentTicketDetailsPage = lazy(() =>
  loadResidentTicketDetailsPage().then((m) => ({ default: m.ResidentTicketDetailsPage })),
);

export const ticketsRoute = [
  {
    path: ticketsRoutes.list,
    // Restore the filters stored earlier in this tab before the page renders, so it only queries the filtered
    // list. Then start this role's page code and first-render queries alongside the session user.
    loader: ({ context, params, request }) => {
      const storedFiltersUrl = params.slug ? getStoredTicketFiltersUrl(new URL(request.url), params.slug) : undefined;
      if (storedFiltersUrl) {
        return redirectFromLoader(request, storedFiltersUrl);
      }

      const startup = context.get(dashboardStartupContext);
      if (startup) {
        import("./ticketListPrefetch")
          .then(({ prefetchTicketListRoute }) => prefetchTicketListRoute(startup, params.slug, request.url))
          .catch(() => {});
      }

      return null;
    },
    shouldRevalidate: shouldRevalidateOnPathChange,
    element: (
      <RolePage
        getPageForRole={(role) => (
          <RequireAuth permission={role === "resident" ? canAccessResidentTickets : canAccessAdminTickets}>
            {role === "resident" ? <ResidentTicketListPage /> : <TicketListPage />}
          </RequireAuth>
        )}
      />
    ),
  },
  {
    path: ticketsRoutes.details,
    // The linked project already says the user's role, so start this role's page code and ticket queries
    // alongside the session user instead of after it.
    loader: ({ context, params }) => {
      const startup = context.get(dashboardStartupContext);
      const ticketId = params.id;
      if (startup && ticketId) {
        whenDashboardProjectReady(startup, params.slug, ({ project, projectApi }) => {
          const { queryClient } = startup;
          const queries = ticketQueries(projectApi, project.id as ProjectId);
          // Failed prefetches leave the error in the cache; the page retries when it mounts.
          if (project.userRole === "resident") {
            void loadResidentTicketDetailsPage();
            queryClient.query(queries.detailsResident(ticketId)).catch(() => {});
            queryClient.infiniteQuery(queries.commentsResident(ticketId)).catch(() => {});

            return;
          }

          void loadTicketDetailsPage();
          queryClient.query(queries.detailsAdmin(ticketId)).catch(() => {});
          queryClient
            .infiniteQuery(
              queries.activitiesInfinite({
                ticketId,
                sortDescending: getLocalStorageValue(LOCAL_STORAGE_KEYS.TICKET_DESCENDING_ACTIVITIES, true),
              }),
            )
            .catch(() => {});
        });
      }

      return null;
    },
    element: (
      <RolePage
        getPageForRole={(role) => (role === "resident" ? <ResidentTicketDetailsPage /> : <TicketDetailsPage />)}
      />
    ),
  },
  { path: "*", element: <ErrorPage status={404} /> },
] satisfies RouteObject[];
